import { latexEscape } from './latexEscape';

export const JAKE_PREAMBLE = String.raw`\documentclass[letterpaper,11pt]{article}
\usepackage[empty]{fullpage}
\usepackage{titlesec}
\usepackage[usenames,dvipsnames]{color}
\usepackage{enumitem}
\usepackage[hidelinks]{hyperref}
\usepackage{fancyhdr}
\usepackage{tabularx}
\input{glyphtounicode}
\pagestyle{fancy}
\fancyhf{}
\fancyfoot{}
\renewcommand{\headrulewidth}{0pt}
\renewcommand{\footrulewidth}{0pt}
\addtolength{\oddsidemargin}{-0.5in}
\addtolength{\evensidemargin}{-0.5in}
\addtolength{\textwidth}{1in}
\addtolength{\topmargin}{-.5in}
\addtolength{\textheight}{1.0in}
\urlstyle{same}
\raggedbottom
\raggedright
\setlength{\tabcolsep}{0in}
\titleformat{\section}{\vspace{-4pt}\scshape\raggedright\large}{}{0em}{}[\color{black}\titlerule \vspace{-5pt}]
\pdfgentounicode=1
\newcommand{\resumeItem}[1]{\item\small{{#1 \vspace{-2pt}}}}
\newcommand{\resumeSubheading}[4]{\vspace{-2pt}\item\begin{tabular*}{0.97\textwidth}[t]{l@{\extracolsep{\fill}}r}\textbf{#1} & #2 \\ \textit{\small#3} & \textit{\small #4} \\\end{tabular*}\vspace{-7pt}}
\newcommand{\resumeProjectHeading}[2]{\item\begin{tabular*}{0.97\textwidth}{l@{\extracolsep{\fill}}r}\small#1 & #2 \\\end{tabular*}\vspace{-7pt}}
\newcommand{\resumeSubItem}[1]{\resumeItem{#1}\vspace{-4pt}}
\renewcommand\labelitemii{$\vcenter{\hbox{\tiny$\bullet$}}$}
\newcommand{\resumeSubHeadingListStart}{\begin{itemize}[leftmargin=0.15in, label={}]}
\newcommand{\resumeSubHeadingListEnd}{\end{itemize}}
\newcommand{\resumeItemListStart}{\begin{itemize}}
\newcommand{\resumeItemListEnd}{\end{itemize}\vspace{-5pt}}`;

function joinNonEmpty(parts, sep) {
  return parts.filter(Boolean).join(sep);
}

// Escape characters that break the URL argument of \href (% comments the line,
// # and & are special, braces/backslash must be balanced/escaped).
function latexEscapeUrl(url) {
  return String(url || '').replace(/([\\%#&{}])/g, '\\$1');
}

function heading(contact, toggles) {
  const name = latexEscape(contact.fullName || 'Your Name');
  const items = [];
  if (toggles.phone && contact.phone) items.push(latexEscape(contact.phone));
  if (contact.email) items.push(String.raw`\href{mailto:${latexEscapeUrl(contact.email)}}{\underline{${latexEscape(contact.email)}}}`);
  if (toggles.linkedin && contact.linkedin) items.push(String.raw`\href{https://${latexEscapeUrl(contact.linkedin)}}{\underline{${latexEscape(contact.linkedin)}}}`);
  if (contact.website) items.push(String.raw`\href{https://${latexEscapeUrl(contact.website)}}{\underline{${latexEscape(contact.website)}}}`);
  return String.raw`\begin{center}
  \textbf{\Huge \scshape ${name}} \\ \vspace{1pt}
  \small ${items.join(' $|$ ')}
\end{center}`;
}

function splitBullets(bullets) {
  if (!bullets) return [];
  return String(bullets)
    .split('\n')
    .map(l => l.replace(/^\s*[•\-*]\s*/, '').trim())
    .filter(Boolean);
}
function dateRange(start, end, isCurrent) {
  const e = isCurrent ? 'Present' : (end || '');
  return joinNonEmpty([start, e], ' -- ');
}
function loc(item) {
  return latexEscape(item.location || '');
}

const hasText = (v) => Boolean((v || '').toString().trim());

function sectionExperience(list) {
  const entries = (list || []).filter(x => hasText(x.role) || hasText(x.company) || splitBullets(x.bullets).length);
  if (entries.length === 0) return '';
  const items = entries.map(x => {
    const bullets = splitBullets(x.bullets);
    const bulletTex = bullets.length
      ? `\n      \\resumeItemListStart\n${bullets.map(b => `        \\resumeItem{${latexEscape(b)}}`).join('\n')}\n      \\resumeItemListEnd`
      : '';
    return `    \\resumeSubheading{${latexEscape(x.role)}}{${latexEscape(dateRange(x.startDate, x.endDate, x.isCurrent))}}{${latexEscape(x.company)}}{${loc(x)}}${bulletTex}`;
  }).join('\n');
  return `\\section{Experience}\n  \\resumeSubHeadingListStart\n${items}\n  \\resumeSubHeadingListEnd`;
}

function sectionEducation(list) {
  const entries = (list || []).filter(x => hasText(x.school) || hasText(x.degree));
  if (entries.length === 0) return '';
  const items = entries.map(x =>
    `    \\resumeSubheading{${latexEscape(x.school)}}{${loc(x)}}{${latexEscape(joinNonEmpty([x.degree, x.minor && `Minor: ${x.minor}`, x.gpa && `GPA: ${x.gpa}`], ', '))}}{${latexEscape(dateRange(x.startDate, x.endDate, x.isCurrent))}}`
  ).join('\n');
  return `\\section{Education}\n  \\resumeSubHeadingListStart\n${items}\n  \\resumeSubHeadingListEnd`;
}

function sectionProjects(list) {
  const entries = (list || []).filter(x => hasText(x.name) || splitBullets(x.relevance).length);
  if (entries.length === 0) return '';
  const items = entries.map(x => {
    const head = `    \\resumeProjectHeading{\\textbf{${latexEscape(x.name)}}}{${latexEscape(x.date)}}`;
    const bullets = splitBullets(x.relevance);
    const bulletTex = bullets.length
      ? `\n      \\resumeItemListStart\n${bullets.map(b => `        \\resumeItem{${latexEscape(b)}}`).join('\n')}\n      \\resumeItemListEnd`
      : '';
    return head + bulletTex;
  }).join('\n');
  return `\\section{Projects}\n  \\resumeSubHeadingListStart\n${items}\n  \\resumeSubHeadingListEnd`;
}

function sectionNamedList(title, list) {
  const entries = (list || []).filter(x => hasText(x.name));
  if (entries.length === 0) return '';
  const items = entries.map(x =>
    `    \\resumeItem{\\textbf{${latexEscape(x.name)}}${x.organization ? ` — ${latexEscape(x.organization)}` : ''}${x.date ? ` \\hfill ${latexEscape(x.date)}` : ''}${x.relevance ? `: ${latexEscape(x.relevance)}` : ''}}`
  ).join('\n');
  return `\\section{${title}}\n  \\resumeItemListStart\n${items}\n  \\resumeItemListEnd`;
}

function sectionSummary(summary) {
  if (!summary || !summary.trim()) return '';
  return `\\section{Summary}\n  \\small{${latexEscape(summary.trim())}}\n\\vspace{2pt}`;
}

function sectionSkills(skills) {
  if (!skills || !skills.trim()) return '';
  const lines = skills.split('\n').map(l => l.trim()).filter(Boolean)
    .map(l => `      \\small{${latexEscape(l)}} \\\\`).join('\n');
  return `\\section{Technical Skills}\n  \\begin{itemize}[leftmargin=0.15in, label={}]\n    \\small{\\item{\n${lines}\n    }}\n  \\end{itemize}`;
}

function renderSection(id, data) {
  switch (id) {
    case 'summary': return sectionSummary(data.summary);
    case 'experience': return sectionExperience(data.experiences);
    case 'education': return sectionEducation(data.educations);
    case 'projects': return sectionProjects(data.projects);
    case 'certifications': return sectionNamedList('Certifications', data.certifications);
    case 'awards': return sectionNamedList('Awards', data.awards);
    case 'skills': return sectionSkills(data.skills);
    default: return '';
  }
}

// Canonical section order — used when a caller doesn't supply its own
// (the editor's live preview) and mirrored by FinalResumePage's initial state.
export const DEFAULT_SECTION_ORDER = [
  'summary', 'experience', 'education', 'projects', 'certifications', 'awards', 'skills',
];

export function generateTex(data, order) {
  const head = heading(data.contact || {}, data.contactToggles || {});
  const sections = (order?.sections || DEFAULT_SECTION_ORDER)
    .map(id => renderSection(id, data))
    .filter(Boolean)
    .join('\n');
  return `${JAKE_PREAMBLE}\n\\begin{document}\n${head}\n${sections}\n\\end{document}\n`;
}
